<template>
    <modal
        size="4xl"
        :open="open"
        @close="$emit('close')"
        :title="`Registrar Outcome na ${providerLabel}`"
    >
        <chargeback-info-grid v-if="chargeback" :chargeback="chargeback" />

        <div class="mt-4 grid gap-4 sm:grid-cols-2">
            <single-select
                :modelValue="form.outcome"
                @update:modelValue="onOutcomeChange"
                label="Outcome"
                :options="outcomeOptions"
                tooltip="O que foi feito com o alerta. Não exige estorno: use notfound, other, resolved etc. quando não houver o que estornar."
            />

            <label
                class="flex items-center space-x-2 self-end pb-2 text-sm text-gray-700 dark:text-white"
            >
                <input
                    type="checkbox"
                    v-model="form.refunded"
                    class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span>Valor estornado ao comprador</span>
            </label>
        </div>

        <p
            v-if="sentOutcome"
            class="mt-3 text-sm text-gray-500 dark:text-gray-300"
        >
            Último outcome enviado: <strong>{{ sentOutcome.value }}</strong>
            <span v-if="sentOutcome.sent_at">em {{ sentOutcome.sent_at }}</span
            >. Enviar de novo substitui a resposta na {{ providerLabel }}.
        </p>

        <p class="mt-4 dark:text-white">
            Confirma o envio deste outcome para a {{ providerLabel }}?
        </p>

        <div class="mt-4 flex justify-end space-x-2">
            <submit-button @click="confirmOutcome" :loading="loading">
                Enviar outcome
            </submit-button>
            <button
                @click="$emit('close')"
                class="px-4 py-2 bg-gray-200 text-gray-800 rounded-md"
            >
                Cancelar
            </button>
        </div>
    </modal>
</template>

<script>
import Modal from "../../layouts/Modal.vue";
import SubmitButton from "../../forms/SubmitButton.vue";
import SingleSelect from "../../forms/SingleSelect.vue";
import ChargebackInfoGrid from "./ChargebackInfoGrid.vue";

// Espelha App\Enum\Ethoca\Outcome: o backend valida a lista por tipo de alerta.
const OUTCOME_LABELS = {
    stopped: "stopped (transação estornada ou cancelada)",
    partially_stopped: "partially_stopped (estorno parcial)",
    missed: "missed (não deu tempo de agir)",
    notfound: "notfound (transação não localizada)",
    other: "other (outro motivo)",
    shipper_contacted: "shipper_contacted (transportadora acionada)",
    account_suspended: "account_suspended (conta do vendedor suspensa)",
    in_progress: "in_progress (em análise)",
    previously_cancelled: "previously_cancelled (já cancelada antes do alerta)",
    resolved: "resolved (disputa resolvida com o comprador)",
    previously_refunded: "previously_refunded (já estornada antes do alerta)",
    unresolved_dispute: "unresolved_dispute (disputa não resolvida)",
};

const OUTCOMES_BY_TYPE = {
    confirmed_fraud: [
        "stopped",
        "partially_stopped",
        "missed",
        "notfound",
        "other",
        "shipper_contacted",
        "account_suspended",
        "in_progress",
        "previously_cancelled",
    ],
    customer_dispute: [
        "resolved",
        "previously_refunded",
        "unresolved_dispute",
        "notfound",
        "other",
    ],
};

// Espelha App\Enum\GlobalCbk\AlertStatus::supportedOutcomes(): a GlobalCbk
// aceita so quatro acoes, iguais para os dois tipos de alerta.
const GLOBAL_CBK_OUTCOMES = [
    "account_suspended",
    "notfound",
    "missed",
    "other",
];

const GLOBAL_CBK_LABELS = {
    account_suspended: "account_suspended (venda identificada e estornada)",
    notfound: "notfound (venda não identificada)",
    missed: "missed (adquirente já marcou como chargeback)",
    other: "other (venda identificada, estorno ainda não feito)",
};

const REFUNDED_BY_DEFAULT = [
    "stopped",
    "partially_stopped",
    "previously_refunded",
];

export default {
    components: {
        Modal,
        SubmitButton,
        SingleSelect,
        ChargebackInfoGrid,
    },
    props: {
        open: Boolean,
        chargeback: Object,
    },
    emits: ["close", "outcome-confirmed"],
    data() {
        return {
            loading: false,
            form: {
                outcome: null,
                refunded: false,
            },
        };
    },
    computed: {
        isGlobalCbk() {
            return this.chargeback?.provider === "globalcbk";
        },
        providerLabel() {
            return this.isGlobalCbk ? "GlobalCbk" : "Ethoca";
        },
        outcomeOptions() {
            if (this.isGlobalCbk) {
                return GLOBAL_CBK_OUTCOMES.map((value) => ({
                    value,
                    label: GLOBAL_CBK_LABELS[value],
                }));
            }

            const values =
                OUTCOMES_BY_TYPE[this.chargeback?.type] ||
                Object.keys(OUTCOME_LABELS);

            return values.map((value) => ({
                value,
                label: OUTCOME_LABELS[value] || value,
            }));
        },
        sentOutcome() {
            return this.chargeback?.data?.outcome || null;
        },
    },
    watch: {
        open(isOpen) {
            if (isOpen) {
                this.resetForm();
            }
        },
    },
    methods: {
        resetForm() {
            const values = this.outcomeOptions.map((option) => option.value);
            let outcome = this.sentOutcome?.value;

            if (!values.includes(outcome)) {
                // Com venda associada o caso tipico e o estorno; sem venda, notfound.
                outcome = this.chargeback?.transaction ? values[0] : "notfound";
            }

            this.form.outcome = outcome;
            this.form.refunded = this.sentOutcome
                ? Boolean(this.sentOutcome.refunded)
                : this.refundedByDefault(outcome);
        },
        onOutcomeChange(value) {
            // O SingleSelect reemite o valor ao sincronizar a prop; so reage a mudanca real.
            if (value === this.form.outcome) {
                return;
            }

            this.form.outcome = value;
            this.form.refunded = this.refundedByDefault(value);
        },
        refundedByDefault(outcome) {
            if (this.isGlobalCbk) {
                return outcome === "account_suspended";
            }

            return REFUNDED_BY_DEFAULT.includes(outcome);
        },
        confirmOutcome() {
            if (!this.form.outcome || this.loading) {
                return;
            }

            this.loading = true;

            axios
                .post(`/api/pre-chargebacks/${this.chargeback.id}/outcome`, {
                    outcome: this.form.outcome,
                    refunded: this.form.refunded,
                })
                .then(({ data }) => {
                    this.$root.toast.success(
                        data.message ||
                            `Outcome enviado para a ${this.providerLabel}.`,
                    );
                    this.$emit("outcome-confirmed");
                })
                .catch((error) => {
                    this.$root.toast.error(
                        "Erro ao marcar outcome: " +
                            (error.response?.data?.message || error.message),
                    );
                })
                .finally(() => {
                    this.loading = false;
                    this.$emit("close");
                });
        },
    },
};
</script>
